/**
 * Google Intelligence Service
 *
 * API client for fetching Google intelligence data (GSC, GA4, Ads, Shopping).
 *
 * PRD: docs/delivery/stories/google-enterprise-integration/prd.md
 * GitHub Issue: #378
 */

import { authFetch } from '@/utils/auth-fetch';

// =========================================================================
// GSC Types
// =========================================================================

export interface GSCOverview {
  totalImpressions: number;
  totalClicks: number;
  avgCTR: number;
  avgPosition: number;
  dateRange: {
    firstDate: string;
    lastDate: string;
    daysWithData: number;
  };
}

export interface GSCPage {
  pageUrl: string;
  pageId: string | null;
  totalImpressions: number;
  totalClicks: number;
  avgCTR: number;
  avgPosition: number;
}

export interface GSCQuery {
  query: string;
  totalImpressions: number;
  totalClicks: number;
  avgCTR: number;
  avgPosition: number;
}

export interface PositionChange {
  query: string;
  pageUrl: string;
  previousPosition: number;
  currentPosition: number;
  change: number;
  improved: boolean;
}

// PageMind-improved pages — per-page GSC deltas + cohort totals.
// Cohort defined by publisher_manifest_records ∩ aeo_page_generations
// (status='published'). Before/after window anchored on pmr.first_seen_at
// per page by default; pass `referenceDate` to override with a single
// calendar date applied to every page. Window: 7|14|28|60|90|120 days.
export interface GSCImprovedPage {
  pageUrl: string;
  productPathId: string;
  deploymentDate: string;         // ISO date — page's own first_seen_at (v1.1)
  referenceDate: string;          // ISO date — analysis anchor (= deploymentDate by default, or override)
  impressionsBefore: number;
  impressionsAfter: number;
  impressionsDelta: number;
  clicksBefore: number;
  clicksAfter: number;
  clicksDelta: number;
  ctrBeforePct: number | null;    // percent (e.g. 4.5 = 4.5%)
  ctrAfterPct: number | null;
  ctrDeltaPp: number | null;      // percentage-point delta
  positionBefore: number | null;
  positionAfter: number | null;
  positionDelta: number | null;
}

export interface GSCImprovedCohortTotals {
  totalPages: number;
  impressionsBefore: number;
  impressionsAfter: number;
  impressionsDelta: number;
  clicksBefore: number;
  clicksAfter: number;
  clicksDelta: number;
  ctrBeforePct: number | null;
  ctrAfterPct: number | null;
}

export interface GSCImprovedPagesResponse {
  pages: GSCImprovedPage[];
  cohort: GSCImprovedCohortTotals;
  window: { days: number; referenceDate: string | null };  // v1.1: echoes the validated override (or null)
  total: number;
  limit: number;
  offset: number;
}

export type GSCImprovedWindowDays = 7 | 14 | 28 | 60 | 90 | 120;

export type GSCImprovedSortBy =
  | 'clicks_delta'
  | 'impressions_delta'
  | 'ctr_delta_pp'
  | 'position_delta'
  | 'clicks_after'
  | 'impressions_after';

// =========================================================================
// GA4 Types
// =========================================================================

export interface GA4Overview {
  totalSessions: number;
  totalUsers: number;
  totalNewUsers: number;
  totalPageViews: number;
  avgEngagementRate: number;
  avgSessionDuration: number;
  avgBounceRate: number;
  totalConversions: number;
  dateRange: {
    firstDate: string;
    lastDate: string;
    daysWithData: number;
  };
}

export interface GA4Page {
  pagePath: string;
  pageId: string | null;
  totalSessions: number;
  totalPageViews: number;
  avgBounceRate: number;
  avgTimeOnPage: number;
  totalConversions: number;
  totalUsers: number;
}

export interface GA4Source {
  source: string;
  medium: string;
  totalSessions: number;
  totalUsers: number;
  avgEngagementRate: number;
  totalConversions: number;
}

export interface GA4TrendPoint {
  date: string;
  sessions: number;
  users: number;
  pageViews: number;
  bounceRate: number;
  conversions: number;
}

// =========================================================================
// Ads Types
// =========================================================================

export interface AdsSearchTerm {
  searchTerm: string;
  campaignName: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  isQuestion: boolean;
  questionType: string | null;
  hasOrganicCoverage: boolean;
}

export interface AdsSummary {
  totalTerms: number;
  totalImpressions: number;
  totalClicks: number;
  totalCost: number;
  totalConversions: number;
  questionCount: number;
  avgCPC: number;
}

export interface ContentGap {
  searchTerm: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  potentialSavings: number;
}

export interface LandingPage {
  landingPageUrl: string;
  pageId: string | null;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  avgQualityScore: number;
  landingPageExperience: string;
}

export interface QSSavings {
  totalPages: number;
  pagesWithImprovement: number;
  estimatedMonthlySavings: number;
  improvements: Array<{
    pageUrl: string;
    previousQS: number;
    currentQS: number;
    monthlySavings: number;
  }>;
}

// =========================================================================
// Shopping Types
// =========================================================================

export interface ShoppingProduct {
  itemId: string;
  productTitle: string;
  brand: string;
  category: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  conversionValue: number;
  hasEnrichedContent: boolean;
  kbEntryId: string | null;
}

export interface ShoppingSummary {
  totalProducts: number;
  totalImpressions: number;
  totalClicks: number;
  totalCost: number;
  totalConversions: number;
  totalRevenue: number;
  avgROAS: number;
  enrichedProductCount: number;
}

export interface ShoppingComparison {
  current: ShoppingSummary;
  previous: ShoppingSummary;
  changes: {
    impressionsChange: number;
    clicksChange: number;
    costChange: number;
    conversionsChange: number;
    revenueChange: number;
    roasChange: number;
  };
}

export interface DisapprovedProduct {
  productId: string;
  title: string;
  link: string;
  issues: Array<{
    code: string;
    description: string;
    severity: string;
  }>;
  kbEntryId: string | null;
}

class GoogleIntelligenceService {
  // =========================================================================
  // GSC Endpoints
  // =========================================================================

  async getGSCOverview(siteId: string, options: {
    startDate?: string;
    endDate?: string;
    device?: string;
    country?: string;
  } = {}): Promise<GSCOverview> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.device) params.append('device', options.device);
    if (options.country) params.append('country', options.country);

    const response = await authFetch(`/api/integrations/google/gsc/overview?${params}`);
    if (!response.ok) throw new Error('Failed to fetch GSC overview');
    return response.json();
  }

  async getGSCPages(siteId: string, options: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ pages: GSCPage[]; total: number }> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await authFetch(`/api/integrations/google/gsc/pages?${params}`);
    if (!response.ok) throw new Error('Failed to fetch GSC pages');
    return response.json();
  }

  async getGSCImprovedPages(siteId: string, options: {
    windowDays?: GSCImprovedWindowDays;
    referenceDate?: string;        // v1.1: YYYY-MM-DD anchor override; omit for per-page deployment date
    urlSearch?: string;
    sortBy?: GSCImprovedSortBy;
    sortDir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  } = {}): Promise<GSCImprovedPagesResponse> {
    const params = new URLSearchParams({ siteId });
    if (options.windowDays) params.append('windowDays', options.windowDays.toString());
    if (options.referenceDate) params.append('referenceDate', options.referenceDate);
    if (options.urlSearch) params.append('urlSearch', options.urlSearch);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortDir) params.append('sortDir', options.sortDir);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await authFetch(`/api/integrations/google/gsc/improved-pages?${params}`);
    if (!response.ok) {
      // Surface the backend's actual error message instead of a generic
      // "failed to fetch" — the backend now returns specific validation
      // errors (e.g. malformed referenceDate → 400 with the parse reason)
      // that the user needs to see. (codex iter-2 PR #1396 minor 2.)
      let backendMessage: string | null = null;
      try {
        const body = await response.json();
        if (body && typeof body.error === 'string') backendMessage = body.error;
      } catch {
        // Body wasn't JSON — fall through to generic.
      }
      throw new Error(
        backendMessage || `Failed to fetch PageMind-improved pages (HTTP ${response.status})`
      );
    }
    return response.json();
  }

  async getGSCQueries(siteId: string, options: {
    startDate?: string;
    endDate?: string;
    pageId?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ queries: GSCQuery[]; total: number }> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.pageId) params.append('pageId', options.pageId);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await authFetch(`/api/integrations/google/gsc/queries?${params}`);
    if (!response.ok) throw new Error('Failed to fetch GSC queries');
    return response.json();
  }

  async getPositionChanges(siteId: string, threshold = 3): Promise<{ changes: PositionChange[] }> {
    const params = new URLSearchParams({ siteId, threshold: threshold.toString() });
    const response = await authFetch(`/api/integrations/google/gsc/position-changes?${params}`);
    if (!response.ok) throw new Error('Failed to fetch position changes');
    return response.json();
  }

  // =========================================================================
  // GA4 Endpoints
  // =========================================================================

  async getGA4Overview(siteId: string, options: {
    startDate?: string;
    endDate?: string;
    device?: string;
    country?: string;
  } = {}): Promise<GA4Overview> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.device) params.append('device', options.device);
    if (options.country) params.append('country', options.country);

    const response = await authFetch(`/api/integrations/google/ga4/overview?${params}`);
    if (!response.ok) throw new Error('Failed to fetch GA4 overview');
    return response.json();
  }

  async getGA4Pages(siteId: string, options: {
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ pages: GA4Page[]; total: number }> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await authFetch(`/api/integrations/google/ga4/pages?${params}`);
    if (!response.ok) throw new Error('Failed to fetch GA4 pages');
    return response.json();
  }

  async getGA4Sources(siteId: string, options: {
    startDate?: string;
    endDate?: string;
    limit?: number;
  } = {}): Promise<{ sources: GA4Source[] }> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.limit) params.append('limit', options.limit.toString());

    const response = await authFetch(`/api/integrations/google/ga4/sources?${params}`);
    if (!response.ok) throw new Error('Failed to fetch GA4 sources');
    return response.json();
  }

  async getGA4Trend(siteId: string, options: {
    startDate?: string;
    endDate?: string;
    pageId?: string;
  } = {}): Promise<{ trend: GA4TrendPoint[] }> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.pageId) params.append('pageId', options.pageId);

    const response = await authFetch(`/api/integrations/google/ga4/trend?${params}`);
    if (!response.ok) throw new Error('Failed to fetch GA4 trend');
    return response.json();
  }

  async getGA4Realtime(siteId: string): Promise<{
    activeUsers: number;
    topPages: Array<{ page: string; activeUsers: number }>;
    snapshotAt: string;
  }> {
    const response = await authFetch(`/api/integrations/google/ga4/realtime?siteId=${siteId}`);
    if (!response.ok) throw new Error('Failed to fetch GA4 realtime');
    return response.json();
  }

  // =========================================================================
  // Ads Endpoints
  // =========================================================================

  async getAdsSearchTerms(siteId: string, options: {
    startDate?: string;
    endDate?: string;
    questionsOnly?: boolean;
    minClicks?: number;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ searchTerms: AdsSearchTerm[]; total: number }> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.questionsOnly) params.append('questionsOnly', 'true');
    if (options.minClicks) params.append('minClicks', options.minClicks.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await authFetch(`/api/integrations/google/ads/search-terms?${params}`);
    if (!response.ok) throw new Error('Failed to fetch Ads search terms');
    return response.json();
  }

  async getAdsSummary(siteId: string, options: {
    startDate?: string;
    endDate?: string;
  } = {}): Promise<AdsSummary> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);

    const response = await authFetch(`/api/integrations/google/ads/summary?${params}`);
    if (!response.ok) throw new Error('Failed to fetch Ads summary');
    return response.json();
  }

  async getContentGaps(siteId: string, minSpend?: number): Promise<{
    gaps: ContentGap[];
    count: number;
    totalPotentialSavings: number;
  }> {
    const params = new URLSearchParams({ siteId });
    if (minSpend) params.append('minSpend', minSpend.toString());

    const response = await authFetch(`/api/integrations/google/ads/content-gaps?${params}`);
    if (!response.ok) throw new Error('Failed to fetch content gaps');
    return response.json();
  }

  async getLandingPages(siteId: string, options: {
    startDate?: string;
    endDate?: string;
    minQS?: number;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ landingPages: LandingPage[]; total: number }> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.minQS) params.append('minQS', options.minQS.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await authFetch(`/api/integrations/google/ads/landing-pages?${params}`);
    if (!response.ok) throw new Error('Failed to fetch landing pages');
    return response.json();
  }

  async getQSSavings(siteId: string): Promise<QSSavings> {
    const response = await authFetch(`/api/integrations/google/ads/qs-savings?siteId=${siteId}`);
    if (!response.ok) throw new Error('Failed to fetch QS savings');
    return response.json();
  }

  // =========================================================================
  // Shopping Endpoints
  // =========================================================================

  async getShoppingProducts(siteId: string, options: {
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    hasKbContent?: boolean;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ products: ShoppingProduct[]; total: number }> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.hasKbContent !== undefined) params.append('hasKbContent', options.hasKbContent.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await authFetch(`/api/integrations/google/shopping/products?${params}`);
    if (!response.ok) throw new Error('Failed to fetch Shopping products');
    return response.json();
  }

  async getShoppingSummary(siteId: string, options: {
    startDate?: string;
    endDate?: string;
  } = {}): Promise<ShoppingSummary> {
    const params = new URLSearchParams({ siteId });
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);

    const response = await authFetch(`/api/integrations/google/shopping/summary?${params}`);
    if (!response.ok) throw new Error('Failed to fetch Shopping summary');
    return response.json();
  }

  async getShoppingComparison(siteId: string, days = 30): Promise<ShoppingComparison> {
    const params = new URLSearchParams({ siteId, days: days.toString() });
    const response = await authFetch(`/api/integrations/google/shopping/comparison?${params}`);
    if (!response.ok) throw new Error('Failed to fetch Shopping comparison');
    return response.json();
  }

  async getDisapprovedProducts(siteId: string, options: {
    limit?: number;
    offset?: number;
  } = {}): Promise<{ products: DisapprovedProduct[]; total: number }> {
    const params = new URLSearchParams({ siteId });
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());

    const response = await authFetch(`/api/integrations/google/shopping/disapproved?${params}`);
    if (!response.ok) throw new Error('Failed to fetch disapproved products');
    return response.json();
  }
}

export const googleIntelligenceService = new GoogleIntelligenceService();
